import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
// Components
import {Container, Box, VStack, useMultiStyleConfig, StackProps, GridItem, HStack} from '@chakra-ui/react'
// Project Components
import {
    Breadcrumb,
    Headline,
    Loader,
    ProductListing,
    ProductTile,
    ProductSkeleton,
    SelectedRefinements,
    FiltersList,
    Pagination,
    HeadlineSkeleton,
    BreadcrumbSkeleton,
    EmptySearchResults,
    SearchResultsHeader,
    StoriesGrid,
    Widget,
    CrossCategoryNavigation,
    Icon,
    Button,
    BannerHalf,
} from '_uiComponents'
// Hooks
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {usePageDesigner, useViewport, useAnalytics, useHeaderSizeWithoutBanner, useCurrentBasket, useSortUrls} from '_customHooks'
import {useCustomSdkProvider} from '_customHooks/use-custom-sdk-provider'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useSearchSuggestions} from '@salesforce/commerce-sdk-react'
// Others
import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {MetaTags, escapeAnalyticsPageDetail, resolveHostLink, absolutizeSfccUrl} from '_utils'
// Constants
import {MAX_AGE, MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '_utils/data/constants'
// Types
import {EventCustomAction, ProductListingVisualization, StorySuggestion, CategoryType, PageIdType, DyAnalyticsMetadata} from '_types'
import {ProductTileAnalyticsProvider, useGlobalConfigState} from '_context'
import {FormattedMessage, useIntl} from 'react-intl'
import {getProductListingPaginationState, getProductListingState} from './utils'

interface errorProps {
    response: {
        status: number
    }
}

// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']

let isFirstTemplateView = true

const ProductList = React.memo(() => {
    const {locale, site} = useMultiSite()
    const headerSize = useHeaderSizeWithoutBanner()
    const headerSizeCollapsed = useHeaderSizeWithoutBanner(true)
    const styles = useMultiStyleConfig('ProductList', {headerSize, headerSizeCollapsed}) as Record<string, StackProps>
    const history = useHistory()
    const location = useLocation()
    const {analyticsEventInit, analyticsFilters, analyticsEventPwaPage} = useAnalytics()
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const {appPreferences} = useGlobalConfigState()
    const {disableEditorialWidget} = appPreferences
    const {formatMessage} = useIntl()

    const einstein = useEinstein()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const {isMobile, isDesktop} = useViewport()
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const {resetPageDesignerPosition, renderPageDesignerRegion} = usePageDesigner()

    /**************** Page State ****************/
    const [inheritedPageId, setInheritedPageId] = useState<{resourceId: string; resourceType: string} | undefined>()
    const [inheritedCategory, setInheritedCategory] = useState<CategoryType | undefined>()
    const userAddedNewFilters = useRef(false)
    const userLoadMore = useRef(false)
    const previousPathRef = useRef<string | undefined>(undefined)
    const isFirstLoad = useRef<boolean>(true)
    const prevCategoryIdRef = useRef<string | undefined>(undefined)
    const prevSearchQueryRef = useRef<string | null | undefined>(undefined)
    const prevSzRef = useRef<string | null>(undefined)

    const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search])

    // Extract refinements from URL query params
    const extractRefinements = () => {
        const refinements = {}

        // Get all 'refine' parameters
        urlParams.getAll('refine').forEach((refine) => {
            // Split by '=' to separate attribute and value
            const [attribute, value] = refine.split('=')
            if (attribute && value) {
                // Decode URI component to handle special characters like '&'
                const decodedValue = decodeURIComponent(value)

                // Check if the value contains multiple values separated by '|'
                if (decodedValue.includes('|')) {
                    const multipleValues = decodedValue.split('|').map((val) => val.replace(/%26/g, '&'))
                    refinements[attribute] = multipleValues
                } else {
                    refinements[attribute] = decodedValue.replace(/%26/g, '&')
                }
            }
        })

        return refinements
    }

    // Get the decoded refinement values
    const refinementValues = extractRefinements()
    searchParams.refine = refinementValues
    const searchQuery = urlParams.get('q')
    const startQuery = urlParams.get('start')
    const endQuery = urlParams.get('sz')
    const promoId = urlParams.get('pmid')
    const cgid = urlParams.get('cgid')
    // const isSearch = location.pathname.includes('search') || !!searchQuery
    const isSearch = location.pathname.includes('search') && !cgid

    searchParams._refine.push(`price=(1..)`)
    if (promoId) {
        searchParams._refine.push(`pmid=${promoId}`)
    }

    searchParams._refine = searchParams._refine.map((refine: string) => refine.replace(/%26/g, '&'))
    // searchParams.offset = startQuery || 0

    const start = startQuery ? parseInt(startQuery, 10) : 0
    const end = endQuery ? parseInt(endQuery, 10) : 0

    searchParams.offset = end

    /**************** Query Actions ****************/
    // _refine is an invalid param for useProductSearch, we don't want to pass it to API call
    const {_refine, ...restOfParams} = searchParams

    const {
        data: sdkData,
        isLoading,
        refetch: refetchCustomSdk,
        isRefetching,
        error,
        isDynamicYieldActive,
        isDynamicYieldEligible,
        isDynamicYieldResolved,
    } = useCustomSdkProvider({
        type: 'product',
        urlSegment: location.pathname,
        params: {
            ...restOfParams,
            refine: _refine,
            isSearch,
        },
        inheritedPageId: inheritedPageId as PageIdType | undefined,
        inheritedCategory: inheritedCategory,
        userLoadMore: userLoadMore,
    })

    const memoizedData = useMemo(
        () => ({
            pageIdData: sdkData?.pageIdData,
            category: sdkData?.categoryData,
            productSearchResult: sdkData?.productData,
            pageComponents: sdkData?.pageDesignerData,
        }),
        [sdkData],
    )

    /* @ts-expect-error This needs a refactor (on @daniele-cam) */
    const {limit, total, sortingOptions: _sortingOptions} = memoizedData.productSearchResult || {}

    const hasListingData = Boolean(memoizedData.productSearchResult)
    const isListingLoading = isLoading || (!hasListingData && !error)

    const showNoResults =
        (isSearch ? !!searchQuery : true) &&
        !!memoizedData.productSearchResult &&
        (!Array.isArray(memoizedData.productSearchResult?.hits) || memoizedData.productSearchResult.hits.length === 0)

    const dataCgid = memoizedData.category?.id ? 'cgid=' + memoizedData.category?.id : ''

    if (memoizedData.category?.c_category.online === false) {
        throw new HTTPNotFound('Category Not Found.')
    }
    let limitByTemplate = 0
    // Default value for the listing layout
    let templateToShow: 'grid2' | 'grid3' = 'grid3'
    // We need to give higher priority to the template defined in the category
    // If the category has a template defined, we use it
    if (memoizedData.category?.c_template === 'search/grid2Template' || searchParams.c_template === 'search/grid2Template') {
        templateToShow = 'grid2'
        limitByTemplate = 20
    } else if (memoizedData.category?.c_template === 'search/grid3Template' || searchParams.c_template === 'search/grid3Template') {
        templateToShow = 'grid3'
        limitByTemplate = 21
    } else if (isSearch) {
        // If we are in search page without a template defined, we override the default value
        templateToShow = 'grid2'
        limitByTemplate = 20
    }

    // initial selected layout and sorting. useful when resetting filters to defaults.
    const defaultLayoutRef = useRef<'grid2' | 'grid3'>(templateToShow)
    const defaultSortingRef = useRef<string>(memoizedData.productSearchResult?.selectedSortingOption)

    // Defining the default value for the listing layout based on category response
    const [listingLayout, setListingLayout] = useState<'grid3' | 'grid2'>(templateToShow)
    // Defining the default filter state based on category response
    const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(memoizedData.category?.c_filtersOpen && isDesktop ? true : false)

    const banner = memoizedData.pageComponents?.find((component) => component.id === 'banner')
    const bannerHalf = memoizedData.pageComponents?.find((component) => component.id === 'banner_half')
    const module1 = memoizedData.pageComponents?.find((component) => component.id === 'module_1')
    const module2 = memoizedData.pageComponents?.find((component) => component.id === 'module_2')
    const module3 = memoizedData.pageComponents?.find((component) => component.id === 'module_3')
    const bannerSticky = memoizedData.pageComponents?.find((component) => component.id === 'banner_sticky')

    const hasBanner = !!banner?.components
    const hasBannerHalf = !!bannerHalf?.components
    const hasModule1 = !!module1?.components
    const hasModule2 = !!module2?.components
    const hasModule3 = !!module3?.components
    const hasBannerSticky = !!bannerSticky?.components

    const {paginationLimit, isInitialProductListingPage, truncateLastProducts} = getProductListingPaginationState({
        currentHitsCount: memoizedData.productSearchResult?.hits?.length ?? 0,
        total,
        start,
        end,
        limitByTemplate,
        hasBannerHalf,
        listingLayout,
    })

    const modulePositions = {
        grid2: {
            banner: 0,
            bannerHalf: 0,
            module1: 2,
            module2: 4,
            module3: 6,
        },
        grid3: {
            banner: 0,
            bannerHalf: 0,
            module1: 3,
            module2: 6,
            module3: 9,
        },
    }

    resetPageDesignerPosition()

    const [visualizationTab, setVisualizationTab] = useState(ProductListingVisualization.PRODUCTS)
    const renderProductListing = useMemo(() => !searchQuery || visualizationTab === ProductListingVisualization.PRODUCTS, [searchQuery, visualizationTab])
    const renderStoriesGrid = useMemo(() => visualizationTab === ProductListingVisualization.STORIES, [visualizationTab])

    // Structured data must use the same hostLink-derived public host as canonical/hreflang.
    const publicHost = resolveHostLink(appPreferences?.hostLink, site?.id)
    const breadcrumbsLdJson =
        publicHost && memoizedData.category?.c_breadCrumbs
            ? JSON.stringify({
                  '@context': 'http://schema.org/',
                  '@type': 'BreadcrumbList',
                  itemListElement: memoizedData.category.c_breadCrumbs.map((item, index) => {
                      const url = typeof item.url === 'string' ? absolutizeSfccUrl(item.url, publicHost) : undefined
                      return {
                          '@type': 'ListItem',
                          position: index + 1,
                          ...(url ? {item: url} : {}),
                          name: item.htmlValue,
                      }
                  }),
              })
            : undefined

    const scrollToTop = () => {
        window.scrollTo(0, 0)
    }

    const getAnalyticsListingType = (): string => {
        if (isSearch) {
            if (!showNoResults) {
                return listingLayout
            }
        } else {
            return `${memoizedData.category?.c_listingType}${shouldRenderBannerHalf ? '-half' : ''}`
        }

        return ''
    }

    const dyAnalyticsMetadata = isDynamicYieldActive
        ? (memoizedData.productSearchResult as {analyticsMetadata?: DyAnalyticsMetadata})?.analyticsMetadata
        : undefined

    const productSortingEngine = isDynamicYieldActive ? 'dynamic_yield' : 'salesforce'
    const productSortingType = isDynamicYieldActive ? dyAnalyticsMetadata?.experienceName : undefined

    const runPwaPageEvent = () => {
        if (window && window.dataLayer) {
            analyticsEventPwaPage({
                pageCategory: 'product-list',
                pagesubCategory: isSearch ? (showNoResults ? 'search-no-hits' : 'search') : memoizedData.category?.c_pageSubCategory,
                pageDetail: isSearch ? searchQuery : escapeAnalyticsPageDetail(memoizedData.category?.c_pageDetail),
                pageLanguage: locale.id.split('-')[0],
                pageStatus: '200',
                listingType: getAnalyticsListingType(),
                menuWidget: memoizedData.category?.c_crossNavigationCategories ? 'cross navigation' : undefined,
                sortingEngine: isDynamicYieldActive ? 'dynamic_yield' : 'salesforce',
                sortingType: dyAnalyticsMetadata?.experienceName,
            })
            if (window.DY && typeof window.DY.API === 'function') {
                window.DY.API('event', {
                    name: 'Search_backeoff_assignment',
                    properties: {
                        Provider: isDynamicYieldActive ? 'DY' : 'SF',
                    },
                })
            }
        }
    }

    const buildDyAffinityData = useCallback(() => {
        if (!searchParams.refine) return []

        return Object.entries(searchParams.refine).flatMap(([attribute, value]) => {
            if (!value) return []
            const valuesArray = Array.isArray(value) ? value : [value]
            const normalizedValues = valuesArray.map((val) => (typeof val === 'string' ? val.replace(/%26/g, '&') : String(val))).filter((val) => val)
            if (normalizedValues.length === 0) return []
            return [{attribute, values: normalizedValues}]
        })
    }, [searchParams.refine])

    const handleDyInformAffinity = useCallback(() => {
        if (typeof window === 'undefined' || !window.DY || typeof window.DY.API !== 'function') {
            return
        }

        const data = buildDyAffinityData()
        if (data.length === 0) return

        try {
            window.DY.API('event', {
                name: 'Inform Affinity',
                properties: {
                    dyType: 'inform-affinity-v1',
                    data,
                },
            })
        } catch (error) {
            console.error('[DY Tracking] Errore nel tracking inform affinity:', error)
        }
    }, [buildDyAffinityData])

    useEffect(() => {
        const handlePopstate = () => {
            void refetchCustomSdk()
        }

        window.addEventListener('popstate', handlePopstate)

        return () => {
            window.removeEventListener('popstate', handlePopstate)
        }
    }, [])

    /**
     * Triggers a custom sdk refetch on these conditions:
     * - filters usage (grid, sort, etc.);
     * - search query changes.
     */
    useEffect(() => {
        if ((history.action === 'REPLACE' || searchQuery) && !userAddedNewFilters.current) {
            void refetchCustomSdk()
        }
    }, [location, searchQuery])

    // Reset isFirstLoad when category or searchQuery changes
    useEffect(() => {
        const currentCategoryId = memoizedData.category?.id
        const currentSearchQuery = searchQuery

        const categoryChanged = currentCategoryId != null && currentCategoryId !== prevCategoryIdRef.current
        const searchQueryChanged = currentSearchQuery != null && prevSearchQueryRef.current !== undefined && currentSearchQuery !== prevSearchQueryRef.current

        if (categoryChanged || searchQueryChanged) {
            isFirstLoad.current = true
        }

        if (currentCategoryId != null) {
            prevCategoryIdRef.current = currentCategoryId
        }
        if (currentSearchQuery != null) {
            prevSearchQueryRef.current = currentSearchQuery
        }
    }, [memoizedData.category?.id, searchQuery])

    useEffect(() => {
        if (memoizedData.category || searchQuery) {
            if ((history.action === 'PUSH' || history.action === 'POP') && (!isSearch ? location.pathname !== previousPathRef.current : true)) {
                if (isLoading || isRefetching) {
                    return
                }
                if (isSearch && !memoizedData.productSearchResult) {
                    return
                }
                setIsFiltersDrawerOpen(memoizedData.category?.c_filtersOpen && isDesktop ? true : false)
                const shouldWaitForDy = !isDynamicYieldResolved || (isDynamicYieldEligible && !dyAnalyticsMetadata?.experienceName)
                if (shouldWaitForDy) {
                    return
                }
                if (isFirstLoad.current) {
                    runPwaPageEvent()
                    isFirstLoad.current = false
                }
                const prevSzNum = prevSzRef.current ? parseInt(prevSzRef.current, 10) : undefined
                const currentSzNum = endQuery ? parseInt(endQuery, 10) : undefined
                const szDecreased = prevSzNum !== undefined && currentSzNum !== undefined && currentSzNum < prevSzNum
                if (szDecreased || memoizedData.category) {
                    scrollToTop()
                }
                prevSzRef.current = endQuery
            }

            previousPathRef.current = location.pathname
        }
    }, [
        memoizedData.category,
        memoizedData.productSearchResult,
        isSearch,
        showNoResults,
        dyAnalyticsMetadata?.experienceName,
        isDynamicYieldEligible,
        isDynamicYieldResolved,
        isLoading,
        isRefetching,
    ])

    useEffect(() => {
        if (memoizedData.pageIdData) {
            setInheritedPageId(memoizedData.pageIdData)
        }
        if (memoizedData.category) {
            setInheritedCategory(memoizedData.category)
        }
    }, [memoizedData.pageIdData, memoizedData.category])

    // Dynamic Yield Recommendation Context for Category and Search pages
    useEffect(() => {
        if (typeof window === 'undefined') return
        if (!isSearch && !memoizedData.category) return

        const dyLocale = locale?.id ? locale.id.replace('-', '_') : undefined
        const categoryTree = memoizedData.category?.parentCategoryTree || []
        const categoryIds = categoryTree.map((node) => node?.id).filter(Boolean) as string[]

        const fallbackCategoryId = memoizedData.category?.id
        const contextData = categoryIds.length > 0 ? categoryIds : fallbackCategoryId ? [fallbackCategoryId] : []

        const context = {
            type: isSearch ? 'OTHER' : 'CATEGORY',
            data: contextData,
            ...(dyLocale ? {lng: dyLocale} : {}),
        }

        window.DY = window.DY || {}
        window.DY.recommendationContext = context
    }, [memoizedData.category, locale?.id])

    useEffect(() => {
        if (
            customer &&
            locale &&
            basket &&
            ((memoizedData.category && memoizedData.productSearchResult) || (isSearch && memoizedData.productSearchResult)) &&
            !window.dataLayer
        ) {
            analyticsEventInit({
                country: locale.id.split('-')[1],
                currency: basket.currency,
                pageCategory: 'product-list',
                pageSubCategory: isSearch ? (showNoResults ? 'search-no-hits' : 'search') : memoizedData.category?.c_pageSubCategory,
                pageDetail: isSearch ? searchQuery : escapeAnalyticsPageDetail(memoizedData.category?.c_pageDetail),
                pageLanguage: locale.id.split('-')[0],
                pageStatus: '200',
                listingType: getAnalyticsListingType(),
                menuWidget: memoizedData.category?.c_crossNavigationCategories ? 'cross navigation' : undefined,
                sortingEngine: isDynamicYieldActive ? 'dynamic_yield' : 'salesforce',
                sortingType: dyAnalyticsMetadata?.experienceName || memoizedData.productSearchResult?.analyticsMetadata?.experienceName,
            })
            if (window && window.DY && typeof window.DY.API === 'function') {
                window.DY.API('event', {
                    name: 'Search_backeoff_assignment',
                    properties: {
                        Provider: isDynamicYieldActive ? 'DY' : 'SF',
                    },
                })
            }
        }
    }, [customer, locale, memoizedData.category, memoizedData.productSearchResult, basket])

    useEffect(() => {
        if (customer.isRegistered && !isFirstTemplateView) {
            void refetchCustomSdk()
            isFirstTemplateView = false
        }
    }, [customer])

    useEffect(() => {
        if (!isRefetching && userLoadMore.current) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    userLoadMore.current = false
                }, 200)
            })
        }
    }, [isRefetching])

    // Apply disallow list to refinements.
    if (memoizedData.productSearchResult?.refinements) {
        memoizedData.productSearchResult.refinements = memoizedData.productSearchResult.refinements.filter(
            ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId),
        )
    }

    const categoryError = error as errorProps | string | undefined

    /**************** Error Handling ****************/
    let errorStatus = typeof categoryError === 'object' && categoryError !== null ? categoryError.response?.status : undefined

    const forbiddenCharacters = /[<>&%()|!$^`?]/
    if (isSearch && forbiddenCharacters.test(searchQuery ?? '')) {
        errorStatus = 400
    }

    if (categoryError) {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        if (categoryError?.toString().includes('504')) {
            errorStatus = 504
        }
    }

    switch (errorStatus) {
        case undefined:
            // No Error.
            break
        case 404:
            throw new HTTPNotFound('Category Not Found.')
        default:
            throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
    }

    /**************** Response Handling ****************/
    if (res) {
        // disable cache if category is using a sorting rule with einstein predictive sort
        if (memoizedData.category?.c_hasPredictiveSort) {
            res.set('Cache-Control', 'no-store')
        } else if (memoizedData.category?.c_dynamicYieldActivation === 'AB' || memoizedData.category?.c_dynamicYieldActivation === 'DY') {
            res.set('Cache-Control', 'no-store')
        } else {
            res.set('Cloudflare-CDN-Cache-Control', `max-age=${MAX_AGE}, s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`)
            // res.set('CDN-Cache-Control', `no-store`)
            res.set('Cache-Control', `max-age=${MAX_AGE}, s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`)
        }

        if (memoizedData.category?.c_canonicalUrl) {
            res.setHeader('Link', `<${memoizedData.category?.c_canonicalUrl}>; rel="canonical"`)
        }
    }

    /**************** Render Variables ****************/

    if (_sortingOptions && _sortingOptions.length > 0 && !_sortingOptions.some((option) => option.id === 'best-matches')) {
        _sortingOptions.push({
            id: 'best-matches',
            label: formatMessage({id: 'sorting.plp.best_matches', defaultMessage: 'Recommended'}),
        })
    }
    const sortUrls = useSortUrls({options: _sortingOptions})
    const sortingOptions = _sortingOptions?.map((option, index) => ({
        ...option,
        value: sortUrls[index],
    }))

    const selectedSortingOption = sortingOptions?.find((option) => option.id === memoizedData.productSearchResult?.selectedSortingOption)

    const {data: searchSuggestionsData} = useSearchSuggestions(
        {
            parameters: {
                q: searchQuery,
                locale: locale.id,
                c_headerSuggestions: false,
            },
        },
        {
            enabled: !!searchQuery,
        },
    )

    const searchSuggestion = useMemo(() => {
        const firstSuggestionAvailable = searchSuggestionsData?.productSuggestions?.suggestedPhrases?.[0]
        if (!firstSuggestionAvailable?.exactMatch) return firstSuggestionAvailable?.phrase
    }, [searchSuggestionsData])

    const suggestedStories = useMemo(() => (searchSuggestionsData?.contentSuggestions?.c_stories as Array<StorySuggestion>) ?? [], [searchSuggestionsData])

    // Toggles filter on and off
    const toggleFilter = useCallback(
        (value: {value: string}, attributeId: string, selected: boolean, allowMultiple: boolean = true, action?: EventCustomAction) => {
            const searchParamsCopy = {...searchParams}
            // Remove the `offset` search param if present.
            delete searchParamsCopy.offset

            // tutte le volte che un utente utilizza un filtro che sia filto o sorting
            // devono essere riportati allo stato iniziale start e sz
            searchParamsCopy.start = 0
            searchParamsCopy.sz = limitByTemplate > 0 ? limitByTemplate : 20

            // If we aren't allowing for multiple selections, simply clear any value set for the
            // attribute, and apply a new one if required.
            if (!allowMultiple) {
                const previousValue = searchParamsCopy.refine[attributeId]
                delete searchParamsCopy.refine[attributeId]

                // Note the loose comparison, for "string != number" checks.
                if (!selected && value.value != previousValue) {
                    searchParamsCopy.refine[attributeId] = value.value
                }
            } else {
                // Get the attibute value as an array.
                let attributeValue = searchParamsCopy.refine[attributeId] || []
                if (Array.isArray(attributeValue)) {
                    attributeValue = attributeValue.map((value) => (typeof value === 'string' ? value.replace(/&/g, '%26') : value))
                } else if (typeof attributeValue === 'string') {
                    attributeValue = attributeValue.replace(/&/g, '%26')
                }

                // Ensure that the value is still converted into an array if it's a `string` or `number`.
                if (typeof attributeValue === 'string') {
                    attributeValue = attributeValue.split('|')
                } else if (typeof attributeValue === 'number') {
                    attributeValue = [attributeValue]
                }

                // Either set the value, or filter the value out.
                if (!selected) {
                    attributeValue.push(value.value)
                } else {
                    // Note the loose comparison, for "string != number" checks.
                    attributeValue = attributeValue?.filter((v) => v != value.value)
                }
                // Update the attribute value in the new search params.
                searchParamsCopy.refine[attributeId] = attributeValue

                // If the update value is an empty array, remove the current attribute key.
                if (searchParamsCopy.refine[attributeId].length === 0) {
                    delete searchParamsCopy.refine[attributeId]
                }
            }

            if (action && value.value) {
                let type
                if (attributeId.toLowerCase().indexOf('category') > -1) {
                    type = 'category'
                }
                if (attributeId.toLowerCase().indexOf('color') > -1) {
                    type = 'color'
                }
                if (attributeId.toLowerCase().indexOf('season') > -1) {
                    type = 'collection'
                }
                if (attributeId.toLowerCase().indexOf('size') > -1) {
                    type = 'size'
                }
                if (attributeId.toLowerCase().indexOf('gender') > -1) {
                    type = 'linea'
                }

                analyticsFilters({action: type, label: value.value.replace(/%26/g, '&'), type: type, value: value.value.replace(/%26/g, '&')})
                // Dynamic Yield attribute filter Tracking
                if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
                    try {
                        window.DY.API('event', {
                            name: 'Filter Items',
                            properties: {
                                dyType: 'filter-items-v1',
                                filterBy: type,
                                filterValue: value.value.replace(/%26/g, '&'),
                            },
                        })
                    } catch (error) {
                        console.error('[DY Tracking] Errore nel tracking attribute filter:', error)
                    }
                }
            }

            if (isSearch) {
                history.push(`/${locale.alias}/search?${stringifySearchParams(searchParamsCopy)}`)
            } else {
                history.push(`${location.pathname}?${stringifySearchParams(searchParamsCopy)}`)
            }
            userAddedNewFilters.current = true
        },
        [searchParams, history, locale, location],
    )

    const handleSorting = (sortValue: string): void => {
        let analyticsSort
        let order = ''
        switch (sortValue) {
            case 'price-ascending':
                analyticsSort = 'price ascend'
                order = 'asc'
                break
            case 'Online Timestamp':
                analyticsSort = 'new arrival'
                order = 'desc'
                break
            case 'best-matches':
                analyticsSort = 'best matches'
                break
            default:
                analyticsSort = 'price descend'
                order = 'desc'
        }

        analyticsFilters({action: 'sort', label: analyticsSort, type: 'sort', value: analyticsSort})
        // Dynamic Yield sorting option Tracking
        if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
            try {
                window.DY.API('event', {
                    name: 'Sort Items',
                    properties: {
                        dyType: 'sort-items-v1',
                        sortBy: analyticsSort,
                        sortOrder: order,
                    },
                })
            } catch (error) {
                console.error('[DY Tracking] Errore nel tracking sorting option:', error)
            }
        }

        const sortUrl = sortingOptions?.find((o) => o.id === sortValue)?.value

        if (sortUrl) {
            applySorting(sortUrl)
        }
    }

    const applySorting = (sortUrl: string): void => {
        const url = new URL(sortUrl, window.location.origin)
        const searchParams = url.searchParams

        if (searchParams.has('start')) {
            searchParams.set('start', '0')
        }
        if (searchParams.has('sz')) {
            searchParams.set('sz', typeof limit === 'number' && limit > 0 ? String(limit) : '20')
        }

        url.search = searchParams.toString()

        history.replace(url.pathname + url.search, {
            position: (history.location.state as {position?: number})?.position,
        })
    }

    const handleListingLayoutChange = (layout: 'grid3' | 'grid2'): void => {
        setListingLayout(layout)

        const adjustUrlParams = (sz: number) => {
            const url = new URL(window.location.href)
            url.searchParams.set('start', '0')
            url.searchParams.set('sz', String(sz))
            return url
        }

        if (layout === 'grid2') {
            const url = adjustUrlParams(20)
            url.searchParams.set('c_template', 'search/grid2Template')
            history.replace(url.pathname + url.search)
        } else if (layout === 'grid3') {
            const url = adjustUrlParams(21)
            url.searchParams.set('c_template', 'search/grid3Template')
            history.replace(url.pathname + url.search)
        }
    }

    useEffect(() => {
        if (memoizedData.category?.c_template === 'search/grid2Template' || searchParams.c_template === 'search/grid2Template') {
            templateToShow = 'grid2'
        } else if (memoizedData.category?.c_template === 'search/grid3Template' || searchParams.c_template === 'search/grid3Template') {
            templateToShow = 'grid3'
        } else if (isSearch) {
            // If we are in search page without a template defined, we override the default value
            templateToShow = 'grid2'
        }
        if (listingLayout !== templateToShow) {
            setListingLayout(templateToShow)
        }
    }, [memoizedData.category])

    useEffect(() => {
        if (userAddedNewFilters.current) {
            void refetchCustomSdk()
            userAddedNewFilters.current = false
            // Update the browser URL to reflect current query params
            const currentURL = new URL(window.location.href)
            window.history.replaceState({}, '', currentURL.toString())
        }
    }, [location])

    const resetFilters = useCallback(() => {
        // reset filters sorting and grid view
        const newSearchParams = {
            ...searchParams,
            refine: [],
            c_template: undefined,
            sort: undefined,
        }
        const newPath = isSearch
            ? `/${locale.alias}/search?${stringifySearchParams(newSearchParams)}`
            : `${location.pathname}?${stringifySearchParams(newSearchParams)}`
        history.push(newPath)
        userAddedNewFilters.current = true

        // apply defaut grid layout
        handleListingLayoutChange(defaultLayoutRef.current)

        // trigger analytics event
        analyticsFilters({action: 'reset'})
    }, [searchParams, history, locale, location, isSearch])

    if (memoizedData.productSearchResult?.refinements) {
        if (memoizedData.category?.c_showCollectionCategoriesAsCategoryFilters) {
            memoizedData.productSearchResult.refinements = memoizedData.productSearchResult.refinements.filter(
                ({attributeId}) => attributeId !== 'c_microcategoryacme',
            )
        } else {
            memoizedData.productSearchResult.refinements = memoizedData.productSearchResult.refinements.filter(
                ({attributeId}) => attributeId !== 'c_macrocategoryacme',
            )
        }
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (memoizedData.productSearchResult) {
            const selectedSorting: string = memoizedData.productSearchResult.selectedSortingOption
            let sortingRule: [{attribute: string; direction: string}] | undefined

            if (selectedSorting === 'Online Timestamp') {
                sortingRule = [{attribute: 'onlineTimestamp', direction: 'descending'}]
            } else if (selectedSorting.startsWith('price')) {
                const [, direction] = selectedSorting.split('-')
                sortingRule = [{attribute: 'price', direction}]
            }

            const itemRange: {start: number} = {start: memoizedData.productSearchResult.offset ?? 0}

            if (isSearch) {
                try {
                    einstein.sendViewSearch(searchQuery, memoizedData.productSearchResult, {sortingRule, itemRange})
                } catch (err) {
                    console.error('Einstein sendViewSearch error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, searchQuery},
                    })
                }
                activeData.sendViewSearch(searchParams, memoizedData.productSearchResult)
            } else {
                try {
                    einstein.sendViewCategory(memoizedData.category, memoizedData.productSearchResult, {sortingRule, itemRange})
                } catch (err) {
                    console.error('Einstein sendViewCategory error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, category: memoizedData.category},
                    })
                }
                activeData.sendViewCategory(searchParams, memoizedData.category, memoizedData.productSearchResult)
            }
        }
    }, [memoizedData.productSearchResult])

    const hasMountedFiltersDrawerRef = useRef(false)
    useEffect(() => {
        if (!hasMountedFiltersDrawerRef.current) {
            hasMountedFiltersDrawerRef.current = true
            if (isFiltersDrawerOpen) {
                analyticsFilters({action: 'open'})
            }
            return
        }

        analyticsFilters({action: isFiltersDrawerOpen ? 'open' : 'close'})
    }, [isFiltersDrawerOpen])

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const refineCounts = Object.entries(searchParams.refine).reduce((acc, [key, value]) => {
        return acc + (Array.isArray(value) ? value.length : 1)
    }, 0)

    const effectiveProductsCount = end > 0 ? end : paginationLimit
    const effectivePaginationLimit = paginationLimit
    const {shouldRenderEditorialModules, shouldRenderBannerHalf, productsToRender, productTileIndexOffset} = getProductListingState({
        hits: memoizedData.productSearchResult?.hits,
        refine: searchParams.refine,
        hasBannerHalf,
        truncateLastProducts,
        maxProductsCount: effectiveProductsCount,
    })
    const shownItemsNo = (shouldRenderBannerHalf ? 4 : 0) + (productsToRender?.length ?? 0)

    const resetFiltersDisabled = Boolean(
        refineCounts === 0 &&
            (!defaultLayoutRef.current || templateToShow === defaultLayoutRef.current) &&
            (!defaultSortingRef.current || selectedSortingOption?.id === defaultSortingRef.current),
    )

    const hasActiveFiltersOrSort = refineCounts > 0 || searchParams.sort !== 'best-matches'

    const metaProps = !isSearch
        ? {
              title: memoizedData.category?.pageTitle ?? '',
              description: memoizedData.category?.pageDescription ?? '',
              keywords: memoizedData.category?.pageKeywords,
              jsonLd: breadcrumbsLdJson,
              enableNoIndex: !!memoizedData.category?.c_seoNoIndex || hasActiveFiltersOrSort,
              hideAlternateUrls: hasActiveFiltersOrSort,
              alternateUrls: memoizedData.category?.c_alternateUrls,
              canonicalUrl: memoizedData.category?.c_canonicalUrl,
          }
        : {
              title: {id: 'global.search.title', defaultMessage: 'Search Results | Brunello Cucinelli'},
              description: 'Site Catalog Acme',
              enableNoIndex: true,
          }

    const renderFiltersButton = (): React.ReactElement => (
        <HStack {...styles.filtersButtonContainer}>
            <Button sx={styles.filtersButton} size="sm" variant={'changelocale'} onClick={() => setIsFiltersDrawerOpen(true)}>
                {refineCounts ? (
                    <FormattedMessage id="label.plp.filters.active" defaultMessage={'FILTERS ( N )'} values={{0: refineCounts}} />
                ) : (
                    <FormattedMessage id="label.plp.filters" defaultMessage="FILTERS" />
                )}
                {<Icon name="IconFilter" size="xs" />}
            </Button>
        </HStack>
    )

    /**
     * When user searchs a product and there are no results
     * it shows empty results page instead of product listing.
     */
    if (!isLoading && !isRefetching && showNoResults)
        return (
            <EmptySearchResults
                searchQuery={isSearch ? searchQuery : memoizedData.category?.name}
                searchSuggestion={searchSuggestion}
                isSearch={isSearch}
                metaProps={metaProps}
            />
        )
    const analyticsName = memoizedData.category?.c_analyticsList?.replace('category-', 'plp-')
    return (
        <>
            <MetaTags {...metaProps} />

            <Box
                className="sf-product-list-page"
                data-testid="sf-product-list-page"
                layerStyle="page"
                data-action="Search-Show"
                data-cgid={dataCgid}
                {...styles.container}
            >
                {(isLoading || isRefetching) && <Loader />}
                <Box className="sr-only" as="span" role="alert" aria-live="polite"></Box>

                <Container variant="plpContainer" {...(isMobile && styles.filtersButtonWrapperSticky)}>
                    <VStack {...styles.wrapper}>
                        {!isSearch &&
                            (isListingLoading ? (
                                <BreadcrumbSkeleton />
                            ) : (
                                memoizedData.category?.c_breadCrumbs && <Breadcrumb items={memoizedData.category?.c_breadCrumbs} />
                            ))}
                        {isSearch && (
                            <SearchResultsHeader
                                searchQuery={searchQuery}
                                productsCount={total ?? 0}
                                storiesCount={suggestedStories.length}
                                tabIndex={visualizationTab}
                                onTabChange={(index) => setVisualizationTab(index)}
                                isLoading={isListingLoading}
                            />
                        )}
                    </VStack>

                    {memoizedData.category?.c_crossNavigationCategories && (
                        <Box {...styles.fullWidth}>
                            <CrossCategoryNavigation categories={memoizedData.category?.c_crossNavigationCategories} variant="top" />
                        </Box>
                    )}

                    <VStack {...styles.wrapper} {...styles.headingWrapper}>
                        {!isSearch &&
                            (isListingLoading ? (
                                <HeadlineSkeleton hasTitle={true} />
                            ) : (
                                memoizedData.category?.name && <Headline title={memoizedData.category?.name} withBanner={hasBanner} />
                            ))}
                        {renderProductListing && (
                            <>
                                {isMobile && renderFiltersButton()}
                                <FiltersList
                                    filtering={{
                                        isOpened: isFiltersDrawerOpen,
                                        setIsFilterListOpened: setIsFiltersDrawerOpen,
                                        filters: memoizedData.productSearchResult?.refinements,
                                        excludedFilters: ['cgid'],
                                        selectedFilters: searchParams.refine,
                                        filterTotals: memoizedData.productSearchResult?.total,
                                        toggleFilter: toggleFilter,
                                        handleReset: resetFilters,
                                        onConfirmFilters: handleDyInformAffinity,
                                        resetFiltersDisabled: resetFiltersDisabled,
                                    }}
                                    layout={{
                                        listingLayout,
                                        setListingLayout: handleListingLayoutChange,
                                    }}
                                    sorting={{
                                        sortingOptions,
                                        selectedSortingOptionLabel: selectedSortingOption,
                                        limit,
                                        handleSorting,
                                    }}
                                />
                            </>
                        )}
                    </VStack>
                </Container>

                <Container variant="plpContainer">
                    {memoizedData.productSearchResult?.selectedRefinements && (
                        <VStack {...styles.wrapper}>
                            <SelectedRefinements
                                filters={memoizedData.productSearchResult?.refinements}
                                toggleFilter={toggleFilter}
                                selectedFilterValues={memoizedData.productSearchResult?.selectedRefinements}
                            />
                        </VStack>
                    )}
                </Container>

                {!isMobile && <Box {...styles.filtersButtonWrapperSticky}>{renderFiltersButton()}</Box>}

                {/* Body  */}
                {renderProductListing && (
                    <ProductTileAnalyticsProvider
                        value={{
                            productSortingEngine,
                            productSortingType,
                        }}
                    >
                        <ProductListing variant={listingLayout}>
                            {isListingLoading
                                ? Array.from({length: 6}, (_, index) => (
                                      <GridItem key={`skeleton-${index}`}>
                                          <ProductSkeleton />
                                      </GridItem>
                                  ))
                                : productsToRender?.map((productSearchItem, idx) => {
                                      const grid3MaxRowItems = isMobile ? 2 : 3
                                      const grid2MaxRowItems = isMobile ? 1 : 2
                                      // remove full width logic for grid2 as per new design ECP-291
                                      // const isFullWidth =
                                      // (isMobile && listingLayout === 'grid3' && (idx + 1) % 3 === 0) ||
                                      // (!isMobile && listingLayout === 'grid2' && (idx + 1) % 5 === 0)
                                      //const isHorizontalTile = Boolean(productSearchItem?.c_images?.horizontal?.url) && !isMobile && listingLayout === 'grid2'
                                      const isFullWidth = isMobile && listingLayout === 'grid3' && (idx + 1) % 3 === 0
                                      const isHorizontalTile = false
                                      // end ECP-291

                                      const showBanner = hasBanner && idx === modulePositions[listingLayout]?.banner
                                      const showBannerHalf = shouldRenderBannerHalf && idx === modulePositions[listingLayout]?.bannerHalf
                                      const showFirstModule = hasModule1 && shouldRenderEditorialModules && idx === modulePositions[listingLayout]?.module1
                                      const showSecondModule = hasModule2 && shouldRenderEditorialModules && idx === modulePositions[listingLayout]?.module2
                                      const showThirdModule = hasModule3 && shouldRenderEditorialModules && idx === modulePositions[listingLayout]?.module3

                                      const isAboveTheFold = shouldRenderBannerHalf
                                          ? false
                                          : (listingLayout === 'grid3' && idx + 1 <= grid3MaxRowItems) ||
                                            (listingLayout === 'grid2' && idx + 1 <= grid2MaxRowItems)

                                      return (
                                          <React.Fragment key={idx}>
                                              {showBanner &&
                                                  renderPageDesignerRegion({
                                                      region: banner,
                                                      analyticsList: memoizedData.category?.c_analyticsList,
                                                      isMainBanner: true,
                                                      listingLayout,
                                                      analyticsName,
                                                  })}

                                              {showBannerHalf && (
                                                  <BannerHalf
                                                      variant={listingLayout}
                                                      products={
                                                          isLoading
                                                              ? Array.from({length: 4}, (_, index) => (
                                                                    <GridItem key={`skeleton-${index}`}>
                                                                        <ProductSkeleton />
                                                                    </GridItem>
                                                                ))
                                                              : memoizedData.productSearchResult?.hits?.slice(0, 4)?.map((productSearchItem, idx) => (
                                                                    <GridItem {...styles.gridItem} key={productSearchItem.productId}>
                                                                        <ProductTile
                                                                            product={{...productSearchItem, id: productSearchItem.productId}}
                                                                            dySlotId={(productSearchItem as {slotId?: string}).slotId}
                                                                            idx={idx}
                                                                            hasLazy={false}
                                                                            listingLayout={listingLayout}
                                                                            isFullWidth={false}
                                                                            analyticsList={isSearch ? 'search' : memoizedData.category?.c_analyticsList}
                                                                            productTileContext={
                                                                                isSearch
                                                                                    ? {type: 'search', searchText: searchQuery ?? undefined}
                                                                                    : memoizedData.category
                                                                                      ? {type: 'category', category: memoizedData.category}
                                                                                      : undefined
                                                                            }
                                                                        />
                                                                    </GridItem>
                                                                ))
                                                      }
                                                      module={renderPageDesignerRegion({
                                                          region: bannerHalf,
                                                          analyticsList: memoizedData?.category?.c_analyticsList,
                                                          listingLayout,
                                                          analyticsName,
                                                      })}
                                                  ></BannerHalf>
                                              )}

                                              {showFirstModule &&
                                                  renderPageDesignerRegion({
                                                      region: module1,
                                                      analyticsList: memoizedData?.category?.c_analyticsList,
                                                      analyticsName,
                                                      listingLayout,
                                                  })}

                                              {showSecondModule &&
                                                  renderPageDesignerRegion({
                                                      region: module2,
                                                      analyticsList: memoizedData?.category?.c_analyticsList,
                                                      analyticsName,
                                                      listingLayout,
                                                  })}

                                              {showThirdModule &&
                                                  renderPageDesignerRegion({
                                                      region: module3,
                                                      analyticsList: memoizedData?.category?.c_analyticsList,
                                                      analyticsName,
                                                      listingLayout,
                                                  })}

                                              <GridItem
                                                  {...styles.gridItem}
                                                  {...(isFullWidth && !isHorizontalTile ? styles.fullWidthTile : {})}
                                                  {...(isFullWidth && isHorizontalTile ? styles.horizontalDeviceWidthTile : {})}
                                                  key={productSearchItem.productId}
                                              >
                                                  <ProductTile
                                                      product={{...productSearchItem, id: productSearchItem.productId}}
                                                      dySlotId={(productSearchItem as {slotId?: string}).slotId}
                                                      idx={idx + productTileIndexOffset}
                                                      hasLazy={!isAboveTheFold}
                                                      listingLayout={listingLayout}
                                                      isFullWidth={isFullWidth}
                                                      analyticsList={isSearch ? 'search' : memoizedData.category?.c_analyticsList}
                                                      productTileContext={
                                                          isSearch
                                                              ? {type: 'search', searchText: searchQuery ?? undefined}
                                                              : memoizedData.category
                                                                ? {type: 'category', category: memoizedData.category}
                                                                : undefined
                                                      }
                                                  />
                                              </GridItem>
                                          </React.Fragment>
                                      )
                                  })}
                            {isRefetching &&
                                !isLoading &&
                                userLoadMore.current &&
                                (() => {
                                    const limitValue = effectivePaginationLimit
                                    const remainingItems = end !== 0 ? total - end : total - limitValue
                                    const showItems = remainingItems < limitValue ? remainingItems : limitValue

                                    return Array.from({length: showItems > 0 ? showItems : 0}, (_, index) => (
                                        <GridItem key={`loading-skeleton-${index}`}>
                                            <ProductSkeleton />
                                        </GridItem>
                                    ))
                                })()}
                            <Pagination
                                start={start}
                                end={isInitialProductListingPage ? 0 : end}
                                total={total}
                                limit={effectivePaginationLimit}
                                userAddedNewFilters={userAddedNewFilters}
                                searchTerm={searchQuery ?? undefined}
                                isRefetching={isRefetching}
                                isLoading={isLoading}
                                userLoadMore={userLoadMore}
                                shownItemsNo={shownItemsNo > 0 ? shownItemsNo : undefined}
                            />
                            {!isSearch && memoizedData.category?.description && <Headline>{memoizedData.category?.description}</Headline>}
                        </ProductListing>
                    </ProductTileAnalyticsProvider>
                )}

                {/* {memoizedData.category?.c_crossNavigationCategories && (
                    <CrossCategoryNavigation categories={memoizedData.category?.c_crossNavigationCategories} variant="bottom" />
                )} */}

                {hasBannerSticky && renderPageDesignerRegion({region: bannerSticky, listingLayout, analyticsName})}

                {renderStoriesGrid && <StoriesGrid stories={suggestedStories} />}

                {(disableEditorialWidget === null ||
                    (typeof disableEditorialWidget !== 'undefined' && disableEditorialWidget !== null && disableEditorialWidget.indexOf('PLP') === -1)) && (
                    <Widget forceHidden={isFiltersDrawerOpen} categoryId={!isSearch ? memoizedData.category?.id : undefined} />
                )}
            </Box>
        </>
    )
})

ProductList.displayName = 'ProductList'

export default ProductList
